import axios from 'axios';
import { API_URL } from '../../config/appConfig';
import {
  saveAccount,
  setActiveAccount,
  getAccountById,
  removeAccount,
  getSavedAccounts,
  clearAllAccounts,
  updateAccountTokens
} from '../../services/accountStore';
// API base URL
// Helper function for consistent API endpoint handling
const buildEndpointUrl = (path) => {
  const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${normalizedPath}`;
};
// Common API endpoint paths
const API_USERS = buildEndpointUrl('/api/users');
// Login user
const login = async (userData) => {
  try {
    // Build proper URL using our utility function
    const loginUrl = `${API_USERS}/login`;
    // Make request with proper headers
    const response = await axios({
      method: 'post',
      url: loginUrl,
      data: userData,
      headers: {
        'Content-Type': 'application/json'
      }
    });
    // Validate token and refresh token
    if (response.data && response.data.token) {
      if (response.data.refreshToken) {
      }
    } else {
    }
    if (response.data) {
      // Make sure the saveCredentials flag is included in the stored data
      const dataToStore = {
        ...response.data,
        saveCredentials: Boolean(userData.saveCredentials) // Ensure this preference is stored with user data
      };
      // If the user wants to save credentials, store in localStorage (persists after browser close)
      if (userData.saveCredentials) {
        localStorage.setItem('user', JSON.stringify(dataToStore));
        // Clean up any session storage to avoid conflicts
        sessionStorage.removeItem('user');
      } else {
        // Store in sessionStorage if they don't want to save credentials (cleared after browser close)
        sessionStorage.setItem('user', JSON.stringify(dataToStore));
        // Clean up any local storage to avoid conflicts
        localStorage.removeItem('user');
      }
      // Also save in multi-account store
      saveAccount(dataToStore);
      setActiveAccount(dataToStore._id || dataToStore.id, dataToStore.schoolId);
      // Double-check that storage worked
      const storedData = userData.saveCredentials 
        ? localStorage.getItem('user') 
        : sessionStorage.getItem('user');
      if (!storedData) {
      }
    } else {
    }
    return response.data;
  } catch (error) {
    throw error;
  }
};
// NEW: Forgot password request (public)
const forgotPasswordRequest = async (email) => {
  try {
    const url = `${API_USERS}/forgot-password`;
    const response = await axios.post(url, { email }, {
      headers: { 'Content-Type': 'application/json' },
      timeout: 10000,
    });
    return response.data;
  } catch (error) {
    // Always return generic success to match backend behavior and avoid enumeration
    return { message: 'If this email is registered, the appropriate administrator has been notified.' };
  }
};
// Logout user - removes this account from saved accounts and switches to next most recent, or fully logs out if none left.
const logout = async (id = null, schoolId = null) => {
  let targetId = id;
  let targetSchoolId = schoolId;
  // If not explicitly passed, try to extract from current active session
  if (!targetId) {
    const localUser = localStorage.getItem('user');
    const sessionUser = sessionStorage.getItem('user');
    try {
      const userData = localUser ? JSON.parse(localUser) : sessionUser ? JSON.parse(sessionUser) : null;
      targetId = userData?._id || userData?.id;
      targetSchoolId = userData?.schoolId ?? null;
    } catch (error) {
    }
  }
  // Find the specific account to logout to revoke its token
  let refreshToken = null;
  if (targetId) {
    const account = getAccountById(targetId, targetSchoolId);
    refreshToken = account?.refreshToken;
  }
  // Call backend logout endpoint to revoke refresh token
  if (refreshToken) {
    try {
      await axios.post(`${API_USERS}/logout`, {
        refreshToken: refreshToken
      }, {
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: 5000 // Short timeout for logout
      });
    } catch (error) {
    }
  }
  // Remove the account from the saved list
  if (targetId) {
    removeAccount(targetId, targetSchoolId);
  }
  // See if there are remaining accounts
  const remaining = getSavedAccounts();
  if (remaining.length === 1) {
    const nextAccount = remaining[0];
    // Set next account as active
    setActiveAccount(nextAccount.id, nextAccount.schoolId);
    const dataToStore = {
      ...nextAccount,
      _id: nextAccount.id
    };
    if (nextAccount.saveCredentials) {
      localStorage.setItem('user', JSON.stringify(dataToStore));
      sessionStorage.removeItem('user');
    } else {
      sessionStorage.setItem('user', JSON.stringify(dataToStore));
      localStorage.removeItem('user');
    }
    // Force page reload to clear store state & switch
    const rolePaths = {
      superadmin: '/superadmin/dashboard',
      admin: '/app/admin',
      teacher: '/app/teacher',
      student: '/app/student',
      parent: '/app/parent',
    };
    const targetPath = rolePaths[nextAccount.role] || '/app/dashboard';
    const cacheBuster = Date.now();
    window.location.replace(`${targetPath}?v=${cacheBuster}`);
    return;
  } else if (remaining.length > 1) {
    localStorage.removeItem('user');
    sessionStorage.removeItem('user');
    localStorage.removeItem('gb_active_account');
    window.location.replace('/login');
    return;
  }
  // No accounts remain: clear all auth/cached data
  localStorage.removeItem('user');
  sessionStorage.removeItem('user');
  localStorage.removeItem('sidebarOpen');
  localStorage.removeItem('currentSection');
  localStorage.removeItem('app_version');
  localStorage.removeItem('app_version_updated_at');
  const keysToKeep = ['installPromptDismissed'];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!keysToKeep.includes(key)) {
      localStorage.removeItem(key);
    }
  }
  sessionStorage.clear();
  document.cookie.split(';').forEach(cookie => {
    const [name] = cookie.trim().split('=');
    if (name) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }
  });
  const cacheBuster = Date.now();
  window.location.replace(`/login?logout=secure&cache=${cacheBuster}`);
};
// Logout all accounts - completely clears ALL application state and revokes all refresh tokens
const logoutAllAccounts = async () => {
  const saved = getSavedAccounts();
  const revocationPromises = saved
    .filter(acc => acc.refreshToken)
    .map(acc => {
      return axios.post(`${API_USERS}/logout`, {
        refreshToken: acc.refreshToken
      }, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 5000
      }).catch(err => {
      });
    });
  await Promise.all(revocationPromises);
  clearAllAccounts();
  localStorage.removeItem('user');
  sessionStorage.removeItem('user');
  localStorage.removeItem('sidebarOpen');
  localStorage.removeItem('currentSection');
  localStorage.removeItem('app_version');
  localStorage.removeItem('app_version_updated_at');
  const keysToKeep = ['installPromptDismissed'];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!keysToKeep.includes(key)) {
      localStorage.removeItem(key);
    }
  }
  sessionStorage.clear();
  document.cookie.split(';').forEach(cookie => {
    const [name] = cookie.trim().split('=');
    if (name) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }
  });
  const cacheBuster = Date.now();
  window.location.replace(`/login?logout=all&cache=${cacheBuster}`);
};
const switchAccount = async (id, schoolId) => {
  const account = getAccountById(id, schoolId);
  if (!account) {
    throw new Error('Account not found in storage');
  }
  const bare = axios.create();
  try {
    await bare.get(`${API_USERS}/me`, {
      headers: { Authorization: `Bearer ${account.token}` },
      timeout: 8000,
    });
  } catch (err) {
    const status = err.response?.status;
    const msg = err.response?.data?.message || '';
    if (status === 401 && (msg.includes('expired') || msg.includes('Token expired'))) {
      // Access token dead — try refresh
      if (account.refreshToken) {
        try {
          const refreshed = await refreshTokenFunction(account.refreshToken);
          account.token = refreshed.token;
          account.refreshToken = refreshed.refreshToken;
          updateAccountTokens(id, schoolId, refreshed.token, refreshed.refreshToken);
        } catch {
          // Both tokens dead — remove stale account
          removeAccount(id, schoolId);
          throw new Error('Η συνεδρία έληξε. Παρακαλώ συνδεθείτε ξανά.');
        }
      } else {
        removeAccount(id, schoolId);
        throw new Error('Η συνεδρία έληξε. Παρακαλώ συνδεθείτε ξανά.');
      }
    }
    // Non-401 errors (network etc.) — let it through, dashboard will handle
  }
  setActiveAccount(id, schoolId);
  const dataToStore = {
    ...account,
    _id: account.id
  };
  if (account.saveCredentials) {
    localStorage.setItem('user', JSON.stringify(dataToStore));
    sessionStorage.removeItem('user');
  } else {
    sessionStorage.setItem('user', JSON.stringify(dataToStore));
    localStorage.removeItem('user');
  }
  // Update lastActiveAt in storage
  saveAccount(dataToStore);
  return dataToStore;
};
// Get user data for current user (to refresh user details)
const getUserData = async (token) => {
  if (!token) {
    throw new Error('Authentication token is required');
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache'
    },
    timeout: 10000
  };
  // Use proper API path
  const meUrl = `${API_USERS}/me`;
  const response = await axios.get(meUrl, config);
  if (response.data) {
    // Update the stored user data but preserve the token
    const updatedUser = {
      ...response.data,
      token: token // Keep the original token
    };
    // Update in both storage locations to ensure it's available
    if (localStorage.getItem('user')) {
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
    if (sessionStorage.getItem('user')) {
      sessionStorage.setItem('user', JSON.stringify(updatedUser));
    }
    return updatedUser;
  }
  return response.data;
};
// Update profile
const updateProfile = async (userData, token) => {
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const profileUrl = `${API_USERS}/profile`;
  const response = await axios.put(profileUrl, userData, config);
  if (response.data) {
    // Make sure we update both storage locations to ensure avatar persists
    // Check if data is in localStorage
    const localData = localStorage.getItem('user');
    if (localData) {
      localStorage.setItem('user', JSON.stringify(response.data));
    }
    // Check if data is in sessionStorage
    const sessionData = sessionStorage.getItem('user');
    if (sessionData) {
      sessionStorage.setItem('user', JSON.stringify(response.data));
    }
    // If no existing storage, use preference from userData
    if (!localData && !sessionData) {
      if (userData.saveCredentials) {
        localStorage.setItem('user', JSON.stringify(response.data));
      } else {
        sessionStorage.setItem('user', JSON.stringify(response.data));
      }
    }
  }
  return response.data;
};
// Automatic token refresh function
const refreshTokenFunction = async (refreshToken) => {
  try {
    const response = await axios.post(`${API_USERS}/refresh-token`, {
      refreshToken: refreshToken
    });
    if (response.data && response.data.token) {
      return response.data;
    } else {
      throw new Error('Invalid refresh response');
    }
  } catch (error) {
    throw error;
  }
};
// Setup axios response interceptor for automatic token expiration handling with refresh
axios.interceptors.response.use(
  (response) => {
    // Return successful responses as-is
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    // Handle 401 errors (token expiration)
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      const errorMessage = error.response.data?.message || '';
      // Check if it's specifically a token expiration error
      if (errorMessage.includes('expired') || errorMessage.includes('Token expired') || 
          (error.response.data?.error === 'jwt expired')) {
        // Mark the request as retried to avoid infinite loops
        originalRequest._retry = true;
        // Get stored user data to retrieve refresh token
        const localUser = localStorage.getItem('user');
        const sessionUser = sessionStorage.getItem('user');
        let user = null;
        let storageType = null;
        try {
          if (localUser) {
            user = JSON.parse(localUser);
            storageType = 'localStorage';
          } else if (sessionUser) {
            user = JSON.parse(sessionUser);
            storageType = 'sessionStorage';
          }
        } catch (parseError) {
        }
        // If we have a refresh token, try to refresh
        if (user && user.refreshToken) {
          try {
            // Call refresh endpoint
            const refreshResponse = await refreshTokenFunction(user.refreshToken);
            // Update stored user data with new tokens from rotation
            const updatedUser = {
              ...user,
              token: refreshResponse.token,
              refreshToken: refreshResponse.refreshToken // Always use new refresh token from rotation
            };
            // Store updated user data back to the same storage location
            if (storageType === 'localStorage') {
              localStorage.setItem('user', JSON.stringify(updatedUser));
            } else if (storageType === 'sessionStorage') {
              sessionStorage.setItem('user', JSON.stringify(updatedUser));
            }
            // Also update the multi-account store so switching works with fresh tokens
            updateAccountTokens(
              updatedUser._id || updatedUser.id,
              updatedUser.schoolId,
              refreshResponse.token,
              refreshResponse.refreshToken
            );
            // Update the original request with the new token
            originalRequest.headers.Authorization = `Bearer ${refreshResponse.token}`;
            // Retry the original request with the new token
            return axios(originalRequest);
          } catch (refreshError) {
            // Refresh token is also expired/invalid, need to logout
            // Clear all stored data
            localStorage.removeItem('user');
            sessionStorage.removeItem('user');
            localStorage.removeItem('sidebarOpen');
            localStorage.removeItem('currentSection');
            // Show user-friendly message for PWA context
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
            const message = isIOS 
              ? 'Your session has fully expired. Please log in again.' 
              : 'Session expired. Redirecting to login...';
            // For PWA/mobile context, give user a moment to see the message
            if ('serviceWorker' in navigator) {
              // Try to show a notification or alert
              if (window.Notification && Notification.permission === 'granted') {
                new Notification('Session Fully Expired', {
                  body: 'Your login session has fully expired. Please log in again.',
                  icon: '/favicon-192x192.png'
                });
              } else {
                alert(message);
              }
            }
            // Redirect to login after a short delay for PWA UX
            setTimeout(() => {
              const cacheBuster = new Date().getTime();
              window.location.replace(`/login?expired=true&cache=${cacheBuster}`);
            }, 1500);
            // Return a rejected promise
            return Promise.reject(new Error('Session fully expired - please log in again'));
          }
        } else {
          // No refresh token available, clear storage and redirect
          localStorage.removeItem('user');
          sessionStorage.removeItem('user');
          localStorage.removeItem('sidebarOpen');
          localStorage.removeItem('currentSection');
          const cacheBuster = new Date().getTime();
          window.location.replace(`/login?expired=true&cache=${cacheBuster}`);
          return Promise.reject(new Error('No refresh token available - please log in again'));
        }
      }
    }
    // Return other errors as-is
    return Promise.reject(error);
  }
);
// Setup axios request interceptor to validate token before sending requests
axios.interceptors.request.use(
  (config) => {
    // Get token from storage
    const localUser = localStorage.getItem('user');
    const sessionUser = sessionStorage.getItem('user');
    let user = null;
    try {
      if (localUser) {
        user = JSON.parse(localUser);
      } else if (sessionUser) {
        user = JSON.parse(sessionUser);
      }
    } catch (error) {
    }
    // If we have a user with a token, validate it's not obviously expired
    // Let requests proceed to server so 401 response interceptor can refresh the token
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);
// Update push notification preference
const updatePushNotificationPreference = async (enabled) => {
  try {
    // Get stored token
    const userData = localStorage.getItem('user') || sessionStorage.getItem('user');
    const user = userData ? JSON.parse(userData) : null;
    const token = user?.token;
    if (!token) {
      throw new Error('No authentication token found');
    }
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };
    const response = await axios.put(`${API_USERS}/push-notification-preference`, {
      enabled
    }, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};
// Get user profile
const getProfile = async () => {
  try {
    const response = await axios.get(API_USERS + '/me');
    return response.data;
  } catch (error) {
    throw error;
  }
};
const authService = {
  login,
  forgotPasswordRequest,
  logout,
  logoutAllAccounts,
  switchAccount,
  refreshToken: refreshTokenFunction,
  updateProfile,
  getProfile,
  getUserData,
  changePassword: updateProfile, // Using updateProfile for profile changes including password
  updatePushNotificationPreference,
};
export default authService;
